import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent
} from "react";
import classNames from "classnames";
import { DateTime } from "luxon";
import Chip, { ChipSet } from "@/components/Chip";
import Dialog from "@/components/Dialog";
import EmptyState from "@/components/EmptyState";
import KeyboardShortcutHint from "@/components/KeyboardShortcutHint";
import SearchInput from "@/components/SearchInput";
import SubSectionHeading from "@/components/SubSectionHeading/SubSectionHeading";
import {
  getActivityDateRange,
  getActivityDisplayName,
  getActivityListIcon,
  getGlobalDisplayName,
  getGlobalHitListItemContent,
  isGlobalExactMatch,
  type ActivityHit,
  type FacilityHit,
  type PersonHit
} from "@/components/AlgoliaSearch";
import { LookupPersonInCcms, parsePgaIdQuery, useCanShowCcmsMemberLookup } from "@/features/person/ccmsLookup";
import type { MaterialNames } from "@/components/material_symbol_names";
import useAmplitude from "@/hooks/useAmplitude";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import {
  inferTypeByUrl,
  matchesRecentFilter,
  recentlyViewedEntityIds,
  RECENTLY_VISITED_PER_TYPE_MAX,
  useRecentlyVisited,
  type RecentlyVisitedItem
} from "@/hooks/useRecentlyVisited";
import { type GlobalSearchDestination } from "@/lib/globalSearchDestinations";
import { groupGlobalSearchResults } from "@/lib/globalSearchGroup";
import { globalSearchIconName } from "@/lib/globalSearchIcon";
import { rankGlobalSearchDestinations, type RankedGlobalSearchDestination } from "@/lib/globalSearchRank";
import { resolveGlobalSearchPath, type GlobalSearchResolveContext } from "@/lib/globalSearchResolve";
import GlobalSearchRow, { type GlobalSearchRowProps } from "./GlobalSearchRow";
import {
  entityHeadline,
  globalSearchFacilityListItemProps,
  globalSearchPersonListItemProps,
  highlightMatchedChars,
  personSupportingText,
  truncatedSupportingText
} from "./entityListItemContent";
import { GLOBAL_SEARCH_ANALYTICS_EVENTS } from "@/layouts/AppLayout/globalSearchAmplitude";
import {
  GLOBAL_SEARCH_FILTER_LABELS,
  GLOBAL_SEARCH_FILTER_ORDER,
  GLOBAL_SEARCH_RECENTS_FILTERS,
  cycleGlobalSearchFilter,
  entitySearchFailedTitle,
  matchesSearchFilter,
  recentsEmptyTitle,
  searchEmptyTitle,
  type GlobalSearchColumn,
  type GlobalSearchRecentsFilter
} from "./globalSearchPanes";
import { GLOBAL_SEARCH_ENTITY_HITS_PER_PAGE } from "./queries";
import {
  formatJobSearchSupportingText,
  getGlobalSearchHitPath,
  isActivityHit,
  isJobHit,
  isPersonHit,
  navigableGlobalSearchHits,
  type GlobalSearchEntityHit,
  type JobHit
} from "./mapGlobalSearchHits";
import { useGlobalSearchEntitySearch } from "./useGlobalSearchEntitySearch";

export { GLOBAL_SEARCH_ENTITY_HITS_PER_PAGE };
export { GLOBAL_SEARCH_FILTER_ORDER } from "./globalSearchPanes";
export { GLOBAL_SEARCH_RECENTS_FILTERS };
export { GLOBAL_SEARCH_MATCH_CLASS, GLOBAL_SEARCH_UNMATCHED_CLASS } from "./entityListItemContent";
export const GLOBAL_SEARCH_RECENTS_LIMIT = RECENTLY_VISITED_PER_TYPE_MAX;
export const GLOBAL_SEARCH_PAGES_SUBTITLE = "Member 360";
export const GLOBAL_SEARCH_ROW_CLASS =
  "scroll-mt-2 rounded-lg cursor-pointer text-left p-2 [content-visibility:auto] [contain-intrinsic-size:auto_64px]";
export const GLOBAL_SEARCH_SELECTED_ROW_CLASS =
  "bg-sys-brand-primary [&_.text-sys-brand-on-surface]:text-sys-brand-on-primary [&_.text-sys-brand-on-surface-variant]:text-sys-brand-on-primary [&_[data-testid=avatar-person-icon]]:text-sys-brand-on-surface-variant [&_[data-testid=avatar-person-icon]_*]:!text-sys-brand-on-surface-variant";
export const GLOBAL_SEARCH_DIALOG_CLASS =
  "global-search-overlay !h-[85vh] !max-h-[85vh] !w-[min(72rem,calc(100vw-2rem))] !min-w-[min(72rem,calc(100vw-2rem))] !max-w-[min(72rem,calc(100vw-2rem))]";
export const GLOBAL_SEARCH_COLUMNS_CLASS = "flex h-full min-h-0 flex-1 flex-col gap-6 overflow-hidden";
export const GLOBAL_SEARCH_COLUMNS_COMPACT_CLASS = "flex min-h-0 w-full min-w-0 flex-col gap-6";
export const GLOBAL_SEARCH_COLUMN_LIST_CLASS = "min-h-0 flex-1 overflow-y-scroll overscroll-contain text-left";
export const GLOBAL_SEARCH_COLUMN_LIST_COMPACT_CLASS = "text-left";
export const GLOBAL_SEARCH_COLUMN_COMPACT_CLASS = "flex w-full min-w-0 flex-col gap-2";
export const GLOBAL_SEARCH_PLACEHOLDER = "Search pages, people, facilities, jobs, and activities";
export const GLOBAL_SEARCH_PLACEHOLDER_COMPACT = "Search Member 360";
export const GLOBAL_SEARCH_COMPACT_SHEET_Z_INDEX = 1100;

const GLOBAL_SEARCH_DIALOG_SHADOW_CSS = `
dialog {
  width: min(72rem, calc(100vw - 2rem)) !important;
  max-width: min(72rem, calc(100vw - 2rem)) !important;
  min-width: min(72rem, calc(100vw - 2rem)) !important;
  max-height: 85vh !important;
  height: 85vh !important;
}
.container {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.scroller {
  overflow: hidden !important;
  overscroll-behavior: contain;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}
.content,
[slot="content"] {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}
::slotted([slot="content"]) {
  display: flex !important;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0 !important;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
}
`;

const globalSearchDialogSheets = new WeakMap<Element, CSSStyleSheet>();

function applyGlobalSearchDialogSize() {
  const host = document.querySelector("md-dialog.global-search-overlay");
  const root = host?.shadowRoot;
  if (!host || !root) return;

  let sheet = globalSearchDialogSheets.get(host);
  if (!sheet) {
    sheet = new CSSStyleSheet();
    root.adoptedStyleSheets.push(sheet);
    globalSearchDialogSheets.set(host, sheet);
  }
  sheet.replaceSync(GLOBAL_SEARCH_DIALOG_SHADOW_CSS);
}

const PEOPLE_GROUP_KEY = "entity-people";
const FACILITIES_GROUP_KEY = "entity-facilities";
const JOBS_GROUP_KEY = "entity-jobs";
const ACTIVITIES_GROUP_KEY = "entity-activities";
const PEOPLE_AND_FACILITIES_GROUP_KEY = "entity-people-and-facilities";
const RECORDS_GROUP_KEY = "entity-records";
const TAB_SWITCH_COPY = "Tab selects a filter. Left and right arrows switch lists.";
const TAB_SWITCH_COPY_COMPACT = "Tab selects a filter.";
const FOOTER_NAV_HINT = "↑↓ to navigate · ←→ to switch lists · Enter to open";
const GLOBAL_SEARCH_TAB_FILTERS_HINT_ID = "global-search-tab-filters-hint";
const ENTITY_SEARCH_GROUP_KEYS = new Set([
  PEOPLE_GROUP_KEY,
  FACILITIES_GROUP_KEY,
  JOBS_GROUP_KEY,
  ACTIVITIES_GROUP_KEY,
  PEOPLE_AND_FACILITIES_GROUP_KEY,
  RECORDS_GROUP_KEY
]);

export type GlobalSearchProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  destinations: GlobalSearchDestination[];
  resolveContext: GlobalSearchResolveContext;
  onNavigate: (path: string) => void;
};

type GlobalSearchPageItem = {
  kind: "page";
  destination: RankedGlobalSearchDestination;
};

type GlobalSearchEntityItem =
  | { kind: "entity"; hit: PersonHit; groupLabel: "People" }
  | { kind: "entity"; hit: FacilityHit; groupLabel: "Facilities" }
  | { kind: "entity"; hit: JobHit; groupLabel: "Jobs" }
  | { kind: "entity"; hit: ActivityHit; groupLabel: "Activities" };

type GlobalSearchListItem = GlobalSearchPageItem | GlobalSearchEntityItem;

type SearchRowContent = Pick<
  GlobalSearchRowProps,
  "iconName" | "picture" | "headline" | "supportingText" | "trailingSupportingText"
>;

function listItemContentToSearchRow(props: ReturnType<typeof globalSearchPersonListItemProps>): SearchRowContent {
  return {
    iconName: props.iconName,
    picture: props.picture,
    headline: props.headline,
    supportingText: props.supportingText,
    trailingSupportingText: props.trailingSupportingText
  };
}

type GlobalSearchColumnGroup = {
  key: string;
  breadcrumbs: string[];
  items: GlobalSearchListItem[];
};

function formatBreadcrumbs(breadcrumbs: string[], breadcrumbIndices: number[][]): JSX.Element | null {
  if (breadcrumbs.length === 0) return null;

  return (
    <>
      {breadcrumbs.map((crumb, index) => (
        <span key={`${crumb}-${index}`}>
          {index > 0 ? " → " : null}
          {highlightMatchedChars(crumb, breadcrumbIndices[index] ?? [])}
        </span>
      ))}
    </>
  );
}

function isComposingKey(event: KeyboardEvent | ReactKeyboardEvent<HTMLInputElement>): boolean {
  const composing = "nativeEvent" in event ? event.nativeEvent.isComposing : event.isComposing;
  return composing || event.keyCode === 229;
}

function optionAccessibleName(destination: { label: string; breadcrumbs: string[] }): string {
  if (destination.breadcrumbs.length === 0) return destination.label;
  return `${destination.label}, ${destination.breadcrumbs.join(" → ")}`;
}

function jobSupportingText(hit: JobHit): string | undefined {
  return formatJobSearchSupportingText({
    facilityName: hit.facility_name,
    consultantName: hit.consultant_name,
    stage: hit.stage,
    status: hit.status
  });
}

function activitySupportingText(hit: ActivityHit): string | undefined {
  return getActivityDateRange(hit);
}

function entityAccessibleName(item: GlobalSearchEntityItem): string {
  if (item.groupLabel === "People") {
    const extra = [item.hit.pga_id, personSupportingText(item.hit)].filter(Boolean).join(", ");
    return extra
      ? `${getGlobalDisplayName(item.hit)}, ${item.groupLabel}, ${extra}`
      : `${getGlobalDisplayName(item.hit)}, ${item.groupLabel}`;
  }
  if (item.groupLabel === "Facilities") {
    const extra = [item.hit.city, item.hit.state, item.hit.section_name].filter(Boolean).join(", ");
    return extra
      ? `${getGlobalDisplayName(item.hit)}, ${item.groupLabel}, ${extra}`
      : `${getGlobalDisplayName(item.hit)}, ${item.groupLabel}`;
  }
  if (item.groupLabel === "Jobs") {
    const extra = jobSupportingText(item.hit);
    return extra ? `${item.hit.name}, ${item.groupLabel}, ${extra}` : `${item.hit.name}, ${item.groupLabel}`;
  }
  const extra = activitySupportingText(item.hit);
  const name = getActivityDisplayName(item.hit);
  return extra ? `${name}, ${item.groupLabel}, ${extra}` : `${name}, ${item.groupLabel}`;
}

function recentAccessibleName(item: RecentlyVisitedItem): string {
  const extra = item.subtext?.trim();
  return extra ? `${item.text}, Recently visited, ${extra}` : `${item.text}, Recently visited`;
}

function searchItemKey(item: GlobalSearchListItem): string {
  if (item.kind === "entity") return `entity:${item.hit.object_type}:${item.hit.objectID}`;
  return `page:${item.destination.id}`;
}

function recentItemKey(item: RecentlyVisitedItem): string {
  return `recent:${item.type}:${item.entityId}`;
}

function selectedIndexForKey<T>(items: T[], selectedKey: string | null, keyFor: (item: T) => string): number {
  if (items.length === 0) return 0;
  if (!selectedKey) return 0;
  const found = items.findIndex((item) => keyFor(item) === selectedKey);
  return found >= 0 ? found : 0;
}

function toGlobalSearchEntityItem(hit: GlobalSearchEntityHit): GlobalSearchEntityItem {
  if (isJobHit(hit)) return { kind: "entity", hit, groupLabel: "Jobs" };
  if (isActivityHit(hit)) return { kind: "entity", hit, groupLabel: "Activities" };
  if (isPersonHit(hit)) return { kind: "entity", hit, groupLabel: "People" };
  return { kind: "entity", hit, groupLabel: "Facilities" };
}

function isEntitySearchGroup(key: string): boolean {
  return ENTITY_SEARCH_GROUP_KEYS.has(key);
}

function isPhotoUrl(value: string | undefined): boolean {
  if (!value) return false;
  return value.startsWith("http") || value.startsWith("/") || value.startsWith("data:");
}

function recentListItemProps(item: RecentlyVisitedItem): {
  picture?: string;
  iconName: MaterialNames;
} {
  const isPerson = item.url.startsWith("/people/");
  if (isPerson) {
    return {
      picture: isPhotoUrl(item.image) ? item.image : undefined,
      iconName: "person"
    };
  }
  return { iconName: (item.image || "history") as MaterialNames };
}

function recentRelativeTime(
  dateTime: RecentlyVisitedItem["dateTime"],
  now: DateTime = DateTime.now()
): string | undefined {
  const parsed = DateTime.fromISO(dateTime);
  const date = parsed.isValid ? parsed : DateTime.fromJSDate(new Date(dateTime));
  if (!date.isValid) return undefined;
  return date.toRelative({ base: now }) ?? undefined;
}

function pageSupportingText(destination: RankedGlobalSearchDestination): JSX.Element {
  if (destination.breadcrumbs.length === 0) {
    return <span className="block min-w-0 truncate text-left">{GLOBAL_SEARCH_PAGES_SUBTITLE}</span>;
  }

  return (
    <span className="block min-w-0 truncate text-left">
      {formatBreadcrumbs(destination.breadcrumbs, destination.match.breadcrumbIndices)}
    </span>
  );
}

function recentsTypeBucket(item: RecentlyVisitedItem): string {
  return item.type || inferTypeByUrl(item.url);
}

function capRecentsPerType(items: RecentlyVisitedItem[]): RecentlyVisitedItem[] {
  const counts = new Map<string, number>();
  return items.filter((item) => {
    const bucket = recentsTypeBucket(item);
    const next = (counts.get(bucket) ?? 0) + 1;
    if (next > GLOBAL_SEARCH_RECENTS_LIMIT) return false;
    counts.set(bucket, next);
    return true;
  });
}

function stopGlobalSearchListWheel(event: { stopPropagation: () => void }) {
  event.stopPropagation();
}

type GlobalSearchEntityKind = "people" | "facilities" | "jobs" | "activities";

const ENTITY_GROUP_META: Record<GlobalSearchEntityKind, { key: string; label: string }> = {
  people: { key: PEOPLE_GROUP_KEY, label: "People" },
  facilities: { key: FACILITIES_GROUP_KEY, label: "Facilities" },
  jobs: { key: JOBS_GROUP_KEY, label: "Jobs" },
  activities: { key: ACTIVITIES_GROUP_KEY, label: "Activities" }
};

function entityHitKind(hit: GlobalSearchEntityHit): GlobalSearchEntityKind {
  if (isJobHit(hit)) return "jobs";
  if (isActivityHit(hit)) return "activities";
  if (isPersonHit(hit)) return "people";
  return "facilities";
}

function buildSearchGroups(
  rankedPages: RankedGlobalSearchDestination[],
  allHits: GlobalSearchEntityHit[],
  people: PersonHit[],
  facilities: FacilityHit[],
  jobs: JobHit[],
  activities: ActivityHit[],
  filter: GlobalSearchRecentsFilter
): GlobalSearchColumnGroup[] {
  const groups: GlobalSearchColumnGroup[] = [];
  const pageDestinations = matchesSearchFilter("page", filter) ? rankedPages : [];

  if (pageDestinations.length > 0) {
    for (const group of groupGlobalSearchResults(pageDestinations)) {
      groups.push({
        key: group.key || `pages-${groups.length}`,
        breadcrumbs: group.breadcrumbs,
        items: group.items.map((destination) => ({ kind: "page", destination }))
      });
    }
  }

  if (filter !== "all") {
    const list: GlobalSearchEntityHit[] =
      filter === "people" ? people : filter === "facilities" ? facilities : filter === "jobs" ? jobs : activities;
    const navigable = navigableGlobalSearchHits(list);
    if (navigable.length > 0) {
      const meta = ENTITY_GROUP_META[filter];
      groups.push({
        key: meta.key,
        breadcrumbs: [meta.label],
        items: navigable.map(toGlobalSearchEntityItem)
      });
    }
    return groups;
  }

  const entities = navigableGlobalSearchHits(allHits);
  const kinds = new Set(entities.map(entityHitKind));
  if (kinds.size >= 2) {
    const includesJobsOrActivities = kinds.has("jobs") || kinds.has("activities");
    groups.push({
      key: includesJobsOrActivities ? RECORDS_GROUP_KEY : PEOPLE_AND_FACILITIES_GROUP_KEY,
      breadcrumbs: [includesJobsOrActivities ? "Records" : "People and facilities"],
      items: entities.map(toGlobalSearchEntityItem)
    });
    return groups;
  }

  if (kinds.size === 1) {
    const only = [...kinds][0] as GlobalSearchEntityKind;
    const meta = ENTITY_GROUP_META[only];
    groups.push({
      key: meta.key,
      breadcrumbs: [meta.label],
      items: entities.map(toGlobalSearchEntityItem)
    });
  }

  return groups;
}

function entityResultListItem(item: GlobalSearchEntityItem, query: string) {
  if (item.groupLabel === "People") {
    return <GlobalSearchRow {...listItemContentToSearchRow(globalSearchPersonListItemProps(item.hit, query))} />;
  }

  if (item.groupLabel === "Jobs") {
    return (
      <GlobalSearchRow
        iconName="business_center"
        headline={entityHeadline(item.hit.name, item.hit.name, query)}
        supportingText={truncatedSupportingText(jobSupportingText(item.hit))}
      />
    );
  }

  if (item.groupLabel === "Activities") {
    const content = getGlobalHitListItemContent(item.hit, query);
    return (
      <GlobalSearchRow
        iconName={content?.iconName ?? getActivityListIcon(item.hit)}
        headline={entityHeadline(content?.headline, getActivityDisplayName(item.hit), query)}
        supportingText={truncatedSupportingText(activitySupportingText(item.hit))}
      />
    );
  }

  return <GlobalSearchRow {...listItemContentToSearchRow(globalSearchFacilityListItemProps(item.hit, query))} />;
}

function TabSwitchHint({ testId }: { testId: string }) {
  return (
    <span className="flex items-center gap-1 text-body-small text-sys-brand-on-surface-variant shrink-0">
      <KeyboardShortcutHint keyLabel="Tab" showModifier={false} size="compact" testId={testId} />
      <span>to select a filter</span>
    </span>
  );
}

const GLOBAL_SEARCH_COLUMN_CLASS = "flex min-h-0 min-w-0 flex-col gap-6 overflow-hidden";

function palettePopupRole(isActive: boolean, hasResults: boolean): "listbox" | "status" | undefined {
  if (!isActive) return undefined;
  return hasResults ? "listbox" : "status";
}

function isGlobalSearchCaptureTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return Boolean(
    target.closest('[data-testid="global-search-search"]') || target.closest('[data-testid="global-search-columns"]')
  );
}

export default function GlobalSearch({
  open,
  onOpenChange,
  destinations,
  resolveContext,
  onNavigate
}: GlobalSearchProps) {
  const [query, setQuery] = useState("");
  const [selectedSearchKey, setSelectedSearchKey] = useState<string | null>(null);
  const [selectedRecentKey, setSelectedRecentKey] = useState<string | null>(null);
  const [filter, setFilter] = useState<GlobalSearchRecentsFilter>("all");
  const [activeColumn, setActiveColumn] = useState<GlobalSearchColumn>("search");
  const [wasOpen, setWasOpen] = useState(open);
  const [hoverArmed, setHoverArmed] = useState(false);
  const [paintResultRows, setPaintResultRows] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const selectedRef = useRef<HTMLDivElement | null>(null);
  const hadCatalogHitsRef = useRef(false);
  const activeColumnRef = useRef<GlobalSearchColumn>(activeColumn);
  activeColumnRef.current = activeColumn;
  const pagesListboxId = useId();
  const recentsListboxId = useId();
  const { trackEvent } = useAmplitude();
  const { isCompactLayout } = useIsCompactLayout();
  const {
    getRecentlyVisitedItems,
    loading: recentsLoading,
    error: recentsError
  } = useRecentlyVisited({ skipListQuery: !open });
  const storedRecents = useMemo(() => {
    if (!open) return [];
    return getRecentlyVisitedItems({ capPerType: null });
  }, [getRecentlyVisitedItems, open]);
  const recentlyViewedIds = useMemo(
    () => ({
      people: recentlyViewedEntityIds(storedRecents, "people"),
      facilities: recentlyViewedEntityIds(storedRecents, "facilities"),
      jobs: recentlyViewedEntityIds(storedRecents, "jobs"),
      activities: recentlyViewedEntityIds(storedRecents, "activities")
    }),
    [storedRecents]
  );
  const canShowCcmsMemberLookup = useCanShowCcmsMemberLookup();
  const {
    allHits,
    peopleHits,
    facilityHits,
    jobHits,
    activityHits,
    pending: entitySearchPending,
    failed: entitySearchFailed
  } = useGlobalSearchEntitySearch(open ? query : "", open, recentlyViewedIds);
  const pgaIdQuery = useMemo(() => parsePgaIdQuery(query), [query]);
  const hasExactPersonMatch = useMemo(
    () =>
      pgaIdQuery
        ? peopleHits.some((hit) => isPersonHit(hit) && hit.pga_id === pgaIdQuery)
        : peopleHits.some((hit) => isGlobalExactMatch(hit, query)),
    [peopleHits, pgaIdQuery, query]
  );

  if (open !== wasOpen) {
    setWasOpen(open);
    setQuery("");
    setSelectedSearchKey(null);
    setSelectedRecentKey(null);
    setFilter("all");
    setActiveColumn(isCompactLayout ? "recents" : "search");
    hadCatalogHitsRef.current = false;
  }

  const rankedResults = useMemo(
    () => rankGlobalSearchDestinations(destinations, query, resolveContext),
    [destinations, query, resolveContext]
  );
  const trimmedQuery = query.trim();
  const showEntityHits = Boolean(trimmedQuery) && !entitySearchPending;
  const recentsActive = isCompactLayout ? !trimmedQuery : activeColumn === "recents";
  const showCcmsLookup =
    canShowCcmsMemberLookup &&
    Boolean(pgaIdQuery) &&
    !entitySearchPending &&
    !hasExactPersonMatch &&
    Boolean(trimmedQuery) &&
    (filter === "all" || filter === "people");
  const recents = useMemo(
    () =>
      capRecentsPerType(
        storedRecents.filter((item) => matchesRecentFilter(item.type || inferTypeByUrl(item.url), filter))
      ),
    [filter, storedRecents]
  );
  const recentsRelativeTimes = useMemo(() => {
    const now = DateTime.now();
    return recents.map((item) => recentRelativeTime(item.dateTime, now));
  }, [recents]);
  const groups = useMemo(
    () =>
      buildSearchGroups(
        rankedResults,
        showEntityHits ? allHits : [],
        showEntityHits ? peopleHits : [],
        showEntityHits ? facilityHits : [],
        showEntityHits ? jobHits : [],
        showEntityHits ? activityHits : [],
        filter
      ),
    [activityHits, allHits, facilityHits, filter, jobHits, peopleHits, rankedResults, showEntityHits]
  );
  const searchResults = useMemo(() => groups.flatMap((group) => group.items), [groups]);
  if (selectedSearchKey === null && searchResults[0]) {
    setSelectedSearchKey(searchItemKey(searchResults[0]));
  }
  if (selectedRecentKey === null && recents[0]) {
    setSelectedRecentKey(recentItemKey(recents[0]));
  }
  const selectedSearchIndex = selectedIndexForKey(searchResults, selectedSearchKey, searchItemKey);
  const selectedRecentIndex = selectedIndexForKey(recents, selectedRecentKey, recentItemKey);
  const hasSearchResults = searchResults.length > 0;
  const hasRecentsResults = recents.length > 0;
  const hasEntityHits = groups.some((group) => isEntitySearchGroup(group.key));
  const hasCatalogHits = searchResults.some((item) => item.kind === "entity" || item.kind === "page");
  const showEntitySearchStatus = Boolean(trimmedQuery) && !hasCatalogHits;
  const activeListboxId = recentsActive ? recentsListboxId : pagesListboxId;
  const activeHasResults = recentsActive ? hasRecentsResults : hasSearchResults;
  const paintedActiveHasResults = paintResultRows && activeHasResults;
  const activeOptionId = !paintedActiveHasResults
    ? undefined
    : recentsActive
      ? `${recentsListboxId}-option-${selectedRecentIndex}`
      : `${pagesListboxId}-option-${selectedSearchIndex}`;
  const pagesListboxLabel = hasEntityHits ? "Search results" : "Pages";
  const recentsListboxLabel =
    filter === "all" ? "Recently visited" : `Recently visited ${GLOBAL_SEARCH_FILTER_LABELS[filter].toLowerCase()}`;

  useEffect(() => {
    if (!open) {
      hadCatalogHitsRef.current = false;
      return;
    }

    const firstCatalogItem = searchResults[0];
    const hasCatalogHits = Boolean(firstCatalogItem);
    const catalogHitsJustAppeared = hasCatalogHits && !hadCatalogHitsRef.current;
    hadCatalogHitsRef.current = hasCatalogHits;
    if (!catalogHitsJustAppeared || !firstCatalogItem) return;

    setSelectedSearchKey((current) => {
      if (current === null) return searchItemKey(firstCatalogItem);
      const selected = searchResults.find((item) => searchItemKey(item) === current);
      if (!selected) return searchItemKey(firstCatalogItem);
      return current;
    });
  }, [open, searchResults]);

  useEffect(() => {
    if (!open) {
      setPaintResultRows(false);
      return;
    }
    let cancelled = false;
    let paintFrame = 0;
    const focusFrame = requestAnimationFrame(() => {
      inputRef.current?.focus();
      paintFrame = requestAnimationFrame(() => {
        if (!cancelled) setPaintResultRows(true);
      });
    });
    const sizeTimer = window.setTimeout(applyGlobalSearchDialogSize, 50);
    return () => {
      cancelled = true;
      cancelAnimationFrame(focusFrame);
      cancelAnimationFrame(paintFrame);
      window.clearTimeout(sizeTimer);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setHoverArmed(false);
      return;
    }
    setHoverArmed(false);
    const armHover = () => setHoverArmed(true);
    window.addEventListener("pointermove", armHover, { once: true });
    return () => window.removeEventListener("pointermove", armHover);
  }, [open]);

  useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: "nearest" });
  }, [selectedSearchIndex, selectedRecentIndex, activeColumn, searchResults, recents]);

  const navigateToSearchItem = useCallback(
    (item: GlobalSearchListItem | undefined) => {
      if (!item) return;

      if (item.kind === "entity") {
        const path = getGlobalSearchHitPath(item.hit);
        if (!path) return;
        trackEvent(GLOBAL_SEARCH_ANALYTICS_EVENTS.clickGlobalSearchResult, {
          backofficeObjectType: item.hit.object_type
        });
        onNavigate(path);
        onOpenChange(false);
        return;
      }

      const path = resolveGlobalSearchPath(item.destination.path, resolveContext, item.destination.fallbackPath);
      trackEvent(GLOBAL_SEARCH_ANALYTICS_EVENTS.clickGlobalSearchResult, {
        backofficeObjectType: "page"
      });
      onNavigate(path);
      onOpenChange(false);
    },
    [onNavigate, onOpenChange, resolveContext, trackEvent]
  );

  const navigateToRecent = useCallback(
    (item: RecentlyVisitedItem | undefined) => {
      if (!item) return;
      trackEvent(GLOBAL_SEARCH_ANALYTICS_EVENTS.clickGlobalSearchRecent, {
        backofficeObjectType: recentsTypeBucket(item)
      });
      onNavigate(item.url);
      onOpenChange(false);
    },
    [onNavigate, onOpenChange, trackEvent]
  );

  const handleCcmsPersonFound = useCallback(
    (pgaId: string) => {
      onNavigate(`/people/${pgaId}`);
      onOpenChange(false);
    },
    [onNavigate, onOpenChange]
  );

  const moveHighlight = useCallback(
    (delta: number) => {
      if (recentsActive) {
        if (recents.length === 0) return;
        const next = Math.min(Math.max(selectedRecentIndex + delta, 0), recents.length - 1);
        const item = recents[next];
        if (item) setSelectedRecentKey(recentItemKey(item));
        return;
      }
      if (searchResults.length === 0) return;
      const next = Math.min(Math.max(selectedSearchIndex + delta, 0), searchResults.length - 1);
      const item = searchResults[next];
      if (item) setSelectedSearchKey(searchItemKey(item));
    },
    [recents, recentsActive, searchResults, selectedRecentIndex, selectedSearchIndex]
  );

  const submitActive = useCallback(() => {
    if (recentsActive) {
      navigateToRecent(recents[selectedRecentIndex]);
      return;
    }
    navigateToSearchItem(searchResults[selectedSearchIndex]);
  }, [
    navigateToRecent,
    navigateToSearchItem,
    recents,
    recentsActive,
    searchResults,
    selectedRecentIndex,
    selectedSearchIndex
  ]);

  const setSearchFilter = useCallback((nextFilter: GlobalSearchRecentsFilter) => {
    setFilter(nextFilter);
    setSelectedSearchKey(null);
    setSelectedRecentKey(null);
    inputRef.current?.focus();
  }, []);

  const activateFilter = useCallback(
    (nextFilter: GlobalSearchRecentsFilter) => {
      trackEvent(GLOBAL_SEARCH_ANALYTICS_EVENTS.clickGlobalSearchFilter, {
        backofficeObjectType: nextFilter
      });
      setSearchFilter(nextFilter);
    },
    [setSearchFilter, trackEvent]
  );

  const filterRef = useRef(filter);
  filterRef.current = filter;

  const handleQueryChange = useCallback(
    (value: string) => {
      setQuery(value);
      setSelectedSearchKey(null);
      if (!value.trim()) {
        setFilter("all");
        setActiveColumn(isCompactLayout ? "recents" : "search");
        return;
      }
      setActiveColumn("search");
    },
    [isCompactLayout]
  );

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (isComposingKey(event)) return;
      if (!isGlobalSearchCaptureTarget(event.target)) return;

      if (event.ctrlKey || event.metaKey || event.altKey) return;

      if (event.key === "Tab") {
        event.preventDefault();
        event.stopPropagation();
        if (event.repeat) return;
        setSearchFilter(cycleGlobalSearchFilter(filterRef.current, event.shiftKey ? -1 : 1));
        return;
      }

      if (event.key === "ArrowRight") {
        if (event.shiftKey || isCompactLayout) return;
        if (activeColumnRef.current === "recents") return;
        event.preventDefault();
        setActiveColumn("recents");
        return;
      }
      if (event.key === "ArrowLeft") {
        if (event.shiftKey || isCompactLayout) return;
        if (activeColumnRef.current === "search") return;
        event.preventDefault();
        setActiveColumn("search");
        return;
      }
      if (event.key === "ArrowDown") {
        event.preventDefault();
        moveHighlight(1);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        moveHighlight(-1);
        return;
      }
      if (event.key === "Enter") {
        const target = event.target as HTMLElement | null;
        if (target?.closest('[data-testid="clear-search"]')) return;
        if (target?.closest('[data-testid="lookup-person-in-ccms"]')) return;
        event.preventDefault();
        if (event.repeat) return;
        submitActive();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [isCompactLayout, moveHighlight, open, setSearchFilter, submitActive]);

  const searchField = open ? (
    <SearchInput
      ref={inputRef}
      query={query}
      onChange={handleQueryChange}
      placeholder={isCompactLayout ? GLOBAL_SEARCH_PLACEHOLDER_COMPACT : GLOBAL_SEARCH_PLACEHOLDER}
      ariaLabel="Global Search"
      pageSearchShortcut={false}
      describedBy={GLOBAL_SEARCH_TAB_FILTERS_HINT_ID}
      listboxId={activeListboxId}
      listboxExpanded={paintedActiveHasResults}
      activeOptionId={activeOptionId}
    />
  ) : null;

  const searchFieldChrome = (
    <div className="w-full min-w-0 font-sans text-body-large font-normal" data-testid="global-search-search">
      {isCompactLayout ? null : <span className="sr-only">Global Search</span>}
      {searchField}
      {isCompactLayout ? (
        <p id={GLOBAL_SEARCH_TAB_FILTERS_HINT_ID} className="sr-only">
          {TAB_SWITCH_COPY_COMPACT}
        </p>
      ) : null}
    </div>
  );

  const columnListClassName = isCompactLayout
    ? GLOBAL_SEARCH_COLUMN_LIST_COMPACT_CLASS
    : GLOBAL_SEARCH_COLUMN_LIST_CLASS;
  const columnListWheel = isCompactLayout ? undefined : stopGlobalSearchListWheel;

  const searchList = (
    <div
      id={pagesListboxId}
      role={palettePopupRole(!recentsActive, hasSearchResults && paintResultRows)}
      aria-label={hasSearchResults && paintResultRows && !recentsActive ? pagesListboxLabel : undefined}
      className={columnListClassName}
      data-testid={hasSearchResults ? "global-search-results" : "global-search-empty"}
      onWheel={columnListWheel}
    >
      {hasSearchResults ? (
        paintResultRows ? (
          <div className="flex flex-col gap-1">
            {groups.map((group, groupIndex) => {
              const startIndex = groups.slice(0, groupIndex).reduce((sum, prior) => sum + prior.items.length, 0);

              return (
                <div
                  key={group.key || `top-level-${groupIndex}`}
                  role="group"
                  aria-label={
                    isEntitySearchGroup(group.key)
                      ? group.breadcrumbs[0]
                      : group.breadcrumbs.length === 0
                        ? GLOBAL_SEARCH_PAGES_SUBTITLE
                        : group.key || GLOBAL_SEARCH_PAGES_SUBTITLE
                  }
                  className="flex flex-col gap-1"
                  data-testid={`global-search-group-${groupIndex}`}
                >
                  {group.items.map((item, offset) => {
                    const index = startIndex + offset;
                    const selected = !recentsActive && index === selectedSearchIndex;

                    return (
                      <div
                        key={item.kind === "entity" ? searchItemKey(item) : item.destination.id}
                        id={`${pagesListboxId}-option-${index}`}
                        ref={selected ? selectedRef : undefined}
                        role={!recentsActive ? "option" : undefined}
                        aria-selected={!recentsActive ? selected : undefined}
                        aria-label={
                          item.kind === "entity"
                            ? entityAccessibleName(item)
                            : optionAccessibleName({
                                label: item.destination.label,
                                breadcrumbs:
                                  item.destination.breadcrumbs.length === 0
                                    ? [GLOBAL_SEARCH_PAGES_SUBTITLE]
                                    : item.destination.breadcrumbs
                              })
                        }
                        data-testid={`global-search-result-${index}`}
                        className={classNames(GLOBAL_SEARCH_ROW_CLASS, selected && GLOBAL_SEARCH_SELECTED_ROW_CLASS)}
                        onMouseEnter={() => {
                          if (!hoverArmed) return;
                          setActiveColumn("search");
                          setSelectedSearchKey(searchItemKey(item));
                        }}
                        onClick={() => navigateToSearchItem(item)}
                      >
                        {item.kind === "entity" ? (
                          entityResultListItem(item, query)
                        ) : (
                          <GlobalSearchRow
                            iconName={globalSearchIconName(item.destination)}
                            headline={highlightMatchedChars(
                              item.destination.label,
                              item.destination.match.labelIndices
                            )}
                            supportingText={pageSupportingText(item.destination)}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        ) : null
      ) : null}
      {entitySearchPending && (showEntitySearchStatus || !hasSearchResults) ? (
        <div className="py-3 text-center text-sys-brand-on-surface-variant">Searching...</div>
      ) : entitySearchFailed && (showEntitySearchStatus || !hasSearchResults) ? (
        <EmptyState title={entitySearchFailedTitle(filter)} caption="Try searching again" />
      ) : !hasSearchResults ? (
        <EmptyState title={searchEmptyTitle(query, filter)} caption="Try a different search" />
      ) : null}
      {showCcmsLookup && pgaIdQuery ? (
        <LookupPersonInCcms pgaId={pgaIdQuery} onFound={handleCcmsPersonFound} variant="inline" />
      ) : null}
    </div>
  );

  const recentsList = (
    <div
      id={recentsListboxId}
      role={palettePopupRole(recentsActive, hasRecentsResults && paintResultRows)}
      aria-label={hasRecentsResults && paintResultRows && recentsActive ? recentsListboxLabel : undefined}
      className={columnListClassName}
      data-testid={
        recentsLoading
          ? "global-search-recents-loading"
          : recentsError
            ? "global-search-recents-error"
            : hasRecentsResults
              ? "global-search-recents"
              : "global-search-recents-empty"
      }
      onWheel={columnListWheel}
    >
      {recentsLoading ? (
        <div className="py-3 text-center text-sys-brand-on-surface-variant">Loading recently visited...</div>
      ) : recentsError ? (
        <EmptyState title="Couldn't load recently visited" caption="Try refreshing the page" />
      ) : hasRecentsResults ? (
        paintResultRows ? (
          <div className="flex flex-col gap-1">
            {recents.map((item, index) => {
              const selected = recentsActive && index === selectedRecentIndex;
              const leading = recentListItemProps(item);

              return (
                <div
                  key={item.url}
                  id={`${recentsListboxId}-option-${index}`}
                  ref={selected ? selectedRef : undefined}
                  role={recentsActive ? "option" : undefined}
                  aria-selected={recentsActive ? selected : undefined}
                  aria-label={recentAccessibleName(item)}
                  data-testid={`global-search-recent-${index}`}
                  className={classNames(GLOBAL_SEARCH_ROW_CLASS, selected && GLOBAL_SEARCH_SELECTED_ROW_CLASS)}
                  onMouseEnter={() => {
                    if (!hoverArmed) return;
                    setActiveColumn("recents");
                    setSelectedRecentKey(recentItemKey(item));
                  }}
                  onClick={() => navigateToRecent(item)}
                >
                  <GlobalSearchRow
                    picture={leading.picture}
                    iconName={leading.iconName}
                    headline={item.text}
                    supportingText={truncatedSupportingText(item.subtext || undefined)}
                    trailingSupportingText={recentsRelativeTimes[index]}
                  />
                </div>
              );
            })}
          </div>
        ) : null
      ) : (
        <EmptyState iconName={isCompactLayout ? "history" : undefined} title={recentsEmptyTitle(filter)} />
      )}
    </div>
  );

  return (
    <Dialog
      open={open}
      closeButtonClassName="!my-0 -mr-2 -mt-2"
      hasFormElements
      sheetZIndex={GLOBAL_SEARCH_COMPACT_SHEET_Z_INDEX}
      onClosed={() => {
        setQuery("");
        setSelectedSearchKey(null);
        setSelectedRecentKey(null);
        setFilter("all");
        setActiveColumn(isCompactLayout ? "recents" : "search");
        hadCatalogHitsRef.current = false;
        setPaintResultRows(false);
        onOpenChange(false);
      }}
      header={{
        title: isCompactLayout ? "Search" : searchFieldChrome
      }}
      className={GLOBAL_SEARCH_DIALOG_CLASS}
      actions={
        isCompactLayout ? null : (
          <span className="text-body-small text-sys-brand-on-surface-variant flex flex-wrap items-center gap-x-3 gap-y-2">
            <span>{FOOTER_NAV_HINT}</span>
            <TabSwitchHint testId="global-search-tab-footer-hint" />
            <KeyboardShortcutHint keyLabel="k" testId="global-search-shortcut-hint-k" />
          </span>
        )
      }
    >
      {open ? (
        <div
          className={isCompactLayout ? GLOBAL_SEARCH_COLUMNS_COMPACT_CLASS : GLOBAL_SEARCH_COLUMNS_CLASS}
          data-testid="global-search-columns"
          data-active-filter={filter}
          data-active-column={recentsActive ? "recents" : "search"}
        >
          {isCompactLayout ? searchFieldChrome : null}
          <div
            className="flex w-full min-w-0 shrink-0 flex-wrap items-center justify-between gap-4"
            data-testid="global-search-pane-tabs"
          >
            <ChipSet>
              {GLOBAL_SEARCH_FILTER_ORDER.map((nextFilter) => (
                <Chip
                  key={nextFilter}
                  label={GLOBAL_SEARCH_FILTER_LABELS[nextFilter]}
                  selected={filter === nextFilter}
                  color="strong"
                  showCheckmark={false}
                  onClick={() => activateFilter(nextFilter)}
                />
              ))}
            </ChipSet>
            {isCompactLayout ? null : (
              <p
                id={GLOBAL_SEARCH_TAB_FILTERS_HINT_ID}
                className="min-w-0 shrink text-right text-body-small text-sys-brand-on-surface-variant"
                data-testid="global-search-tab-filters-hint"
              >
                {TAB_SWITCH_COPY}
              </p>
            )}
          </div>
          {isCompactLayout ? (
            recentsActive ? (
              <section
                className={GLOBAL_SEARCH_COLUMN_COMPACT_CLASS}
                data-testid="global-search-recents-column"
                aria-label="Recently Visited"
              >
                <div className="shrink-0">
                  <SubSectionHeading>Recently Visited</SubSectionHeading>
                </div>
                {recentsList}
              </section>
            ) : (
              <section
                className={GLOBAL_SEARCH_COLUMN_COMPACT_CLASS}
                data-testid="global-search-pages-column"
                aria-label="Pages"
              >
                <div className="shrink-0">
                  <SubSectionHeading>{hasEntityHits ? "Search Results" : "Pages"}</SubSectionHeading>
                </div>
                {searchList}
              </section>
            )
          ) : (
            <div className="min-h-0 flex-1 overflow-hidden grid grid-cols-2 gap-6">
              <section
                className={GLOBAL_SEARCH_COLUMN_CLASS}
                data-testid="global-search-pages-column"
                aria-label="Pages"
              >
                <div className="shrink-0">
                  <SubSectionHeading>{hasEntityHits ? "Search Results" : "Pages"}</SubSectionHeading>
                </div>
                {searchList}
              </section>
              <section
                className={classNames(GLOBAL_SEARCH_COLUMN_CLASS, "border-l border-sys-brand-outline-variant pl-6")}
                data-testid="global-search-recents-column"
                aria-label="Recently Visited"
              >
                <div className="shrink-0">
                  <SubSectionHeading>Recently Visited</SubSectionHeading>
                </div>
                {recentsList}
              </section>
            </div>
          )}
        </div>
      ) : null}
    </Dialog>
  );
}
